import { isEmpty, forEach, isObjectLike, isBoolean, map } from 'lodash';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import {
    COLLECTION_FINANCE,
    CALCULATOR_FINANCE,
    MODEL_FINANCE,
    VEHICLE_FINANCE,
} from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import * as PurchaseMethods from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';
import FinanceModel from 'commons-vehicle/PurchaseMethods/models/FinanceModel.js';
import TermModel from 'commons-vehicle/PurchaseMethods/models/TermModel.js';
import { resetTermFromOriginData } from 'commons-vehicle/PurchaseMethods/services/TermService/TermService.js';
/* eslint import/prefer-default-export: 0 */

function getFinanceTerms(dataOrigin, termsData) {
    return map(termsData, (termData, index) => {
        return resetTermFromOriginData(dataOrigin, termData, index);
    });
}

function resetFinanceFromCollectionData(collectionFinanceData, purchaseMethod, selected) {
    let annualKMAllowed = 0;
    let endTermKMCost = 0;
    let financeData;

    let newPurchaseMethods = purchaseMethod;
    if (purchaseMethod === PurchaseMethods.LEASE && collectionFinanceData[COLLECTION_FINANCE.LEASE]) {
        financeData = collectionFinanceData[COLLECTION_FINANCE.LEASE][COLLECTION_FINANCE.INDEX];
        if (financeData[COLLECTION_FINANCE.LEASE_ANNUAL_KM]) {
            annualKMAllowed = financeData[COLLECTION_FINANCE.LEASE_ANNUAL_KM];
        }
        if (financeData[COLLECTION_FINANCE.LEASE_END_TERM_KM_COST]) {
            endTermKMCost = financeData[COLLECTION_FINANCE.LEASE_END_TERM_KM_COST];
        }
    } else if (purchaseMethod === PurchaseMethods.FINANCE && collectionFinanceData[COLLECTION_FINANCE.FINANCE]) {
        financeData = collectionFinanceData[COLLECTION_FINANCE.FINANCE][COLLECTION_FINANCE.INDEX];
    } else {
        newPurchaseMethods = PurchaseMethods.FINANCE;
    }

    let terms = [new TermModel()];
    let selectedTerm = 0;
    if (financeData) {
        terms = getFinanceTerms(DataOrigins.MODELS_COLLECTION, financeData[COLLECTION_FINANCE.TERMS]);
        selectedTerm = financeData[COLLECTION_FINANCE.BEST_TERM_WITH_CASH];
    }

    const purchaseMethodValue =
        newPurchaseMethods === PurchaseMethods.LEASE ? PURCHASE_METHODS.LEASE : PURCHASE_METHODS.FINANCE;

    return new FinanceModel({
        purchaseMethod: newPurchaseMethods,
        purchaseMethodValue,
        annualKMAllowed,
        endTermKMCost,
        selected: selected && selectedTerm > 0,
        available: false,
        terms,
        selectedTerm,
    });
}

function resetFinanceFromCalculatorData(calculatorFinanceData, purchaseMethod, selected) {
    let terms = [new TermModel()];
    let selectedTerm = 0;

    if (!calculatorFinanceData[CALCULATOR_FINANCE.VEHICLE]) {
        return new FinanceModel({
            purchaseMethod: PurchaseMethods.FINANCE,
            purchaseMethodValue: PURCHASE_METHODS.FINANCE,
            annualKMAllowed: 0,
            endTermKMCost: 0,
            selected: false,
            available: false,
            terms,
            selectedTerm,
        });
    }

    let financeData;
    let newPurchaseMethods = purchaseMethod;
    if (
        purchaseMethod === PurchaseMethods.LEASE &&
        calculatorFinanceData[CALCULATOR_FINANCE.VEHICLE][CALCULATOR_FINANCE.LEASE]
    ) {
        financeData = calculatorFinanceData[CALCULATOR_FINANCE.VEHICLE][CALCULATOR_FINANCE.LEASE];
    } else if (
        purchaseMethod === PurchaseMethods.FINANCE &&
        calculatorFinanceData[CALCULATOR_FINANCE.VEHICLE][CALCULATOR_FINANCE.FINANCE]
    ) {
        financeData = calculatorFinanceData[CALCULATOR_FINANCE.VEHICLE][CALCULATOR_FINANCE.FINANCE];
    } else {
        newPurchaseMethods = PurchaseMethods.FINANCE;
    }

    if (financeData) {
        terms = getFinanceTerms(DataOrigins.PAYMENT_CALCULATOR, financeData);
        selectedTerm = terms.length > 0 ? terms[terms.length - 1].term : 0;
    }

    const purchaseMethodValue =
        newPurchaseMethods === PurchaseMethods.LEASE ? PURCHASE_METHODS.LEASE : PURCHASE_METHODS.FINANCE;

    return new FinanceModel({
        purchaseMethod: newPurchaseMethods,
        purchaseMethodValue,
        annualKMAllowed: 0,
        endTermKMCost: 0,
        selected: selected && selectedTerm > 0,
        available: false,
        terms,
        selectedTerm,
    });
}

function resetFinanceFromModelData(modelFinanceData, purchaseMethod, selected) {
    let financeKMData;

    const financeDataLease = modelFinanceData[MODEL_FINANCE.LEASE] ? MODEL_FINANCE.LEASE : VEHICLE_FINANCE.LEASE;
    const financeDataFinance = modelFinanceData[MODEL_FINANCE.FINANCE]
        ? MODEL_FINANCE.FINANCE
        : VEHICLE_FINANCE.FINANCE;
    let newPurchaseMethods = purchaseMethod;
    if (purchaseMethod === PurchaseMethods.LEASE && modelFinanceData[financeDataLease]) {
        financeKMData = modelFinanceData[financeDataLease];
    } else if (purchaseMethod === PurchaseMethods.FINANCE && modelFinanceData[financeDataFinance]) {
        financeKMData = modelFinanceData[financeDataFinance];
    } else {
        newPurchaseMethods = PurchaseMethods.FINANCE;
    }

    let annualKMAllowed = 0;
    let endTermKMCost = 0;
    let financeData;
    if (financeKMData) {
        financeData = financeKMData[MODEL_FINANCE.INDEX];
        if (financeData[MODEL_FINANCE.LEASE_ANNUAL_KM]) {
            annualKMAllowed = financeData[MODEL_FINANCE.LEASE_ANNUAL_KM];
        }
        if (financeData[MODEL_FINANCE.LEASE_END_TERM_KM_COST]) {
            endTermKMCost = financeData[MODEL_FINANCE.LEASE_END_TERM_KM_COST];
        }
    }

    let terms = [new TermModel()];
    let selectedTerm = 0;
    const kmPlans = [];
    if (financeData) {
        terms = getFinanceTerms(DataOrigins.MODEL_YEAR_POWERS_TRIMS, financeData[MODEL_FINANCE.TERMS]);
        selectedTerm = terms.length > 0 ? terms[terms.length - 1].term : 0;

        if (financeKMData.length > 1) {
            forEach(financeKMData, (financeKM) => {
                kmPlans.push({
                    annualKMAllowed: financeKM[MODEL_FINANCE.LEASE_ANNUAL_KM]
                        ? financeKM[MODEL_FINANCE.LEASE_ANNUAL_KM]
                        : 0,
                    endTermKMCost: financeKM[MODEL_FINANCE.LEASE_END_TERM_KM_COST]
                        ? financeKM[MODEL_FINANCE.LEASE_END_TERM_KM_COST]
                        : 0,
                    terms: getFinanceTerms(DataOrigins.MODEL_YEAR_POWERS_TRIMS, financeKM[MODEL_FINANCE.TERMS]),
                });
            });
        }
    }

    const purchaseMethodValue =
        newPurchaseMethods === PurchaseMethods.LEASE ? PURCHASE_METHODS.LEASE : PURCHASE_METHODS.FINANCE;

    return new FinanceModel({
        purchaseMethod: newPurchaseMethods,
        purchaseMethodValue,
        annualKMAllowed,
        endTermKMCost,
        selected: selected && selectedTerm > 0,
        available: false,
        terms,
        selectedTerm,
        kmPlans,
    });
}

export function resetFinanceFromOriginData(dataOrigin, originFinanceData, purchaseMethod, selected) {
    if (isEmpty(originFinanceData) || !isObjectLike(originFinanceData)) {
        return new FinanceModel();
    }

    const updatedSelected = isBoolean(selected) ? selected : false;

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            return resetFinanceFromCollectionData(originFinanceData, purchaseMethod, updatedSelected);

        case DataOrigins.PAYMENT_CALCULATOR:
            return resetFinanceFromCalculatorData(originFinanceData, purchaseMethod, updatedSelected);

        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            return resetFinanceFromModelData(originFinanceData, purchaseMethod, updatedSelected);

        case DataOrigins.VEHICLE_PAYMENTS:
            return resetFinanceFromModelData(originFinanceData, purchaseMethod, updatedSelected);

        default:
            return new FinanceModel();
    }
}

export default {
    getFinanceTerms,
    resetFinanceFromCollectionData,
    resetFinanceFromCalculatorData,
    resetFinanceFromModelData,
    resetFinanceFromOriginData,
};
